import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FAQ = () => {
  const navigate = useNavigate();
  const [openRow, setOpenRow] = useState<number | null>(null);

  const faqs = [
    {
      question: 'What is AI text-to-speech technology?',
      answer: 'AI text-to-speech (TTS) technology converts written text into natural-sounding speech using advanced neural networks and machine learning algorithms. It creates human-like voices that can be used for various applications.',
      color: 'from-purple-500 to-indigo-500',
      bgColor: 'bg-purple-100 dark:bg-purple-900/20',
      iconColor: 'text-purple-500 dark:text-purple-400'
    },
    {
      question: 'How does neural text-to-speech work?',
      answer: 'Neural TTS uses deep learning models to analyze and understand text patterns, generating speech that mimics human voice characteristics, including intonation, rhythm, and emotional expression.',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-100 dark:bg-blue-900/20',
      iconColor: 'text-blue-500 dark:text-blue-400'
    },
    {
      question: 'What are the benefits of using AI voices over human voiceovers?',
      answer: 'AI voices offer consistent quality, 24/7 availability, cost-effectiveness, and quick turnaround times. They are ideal for projects requiring multiple iterations or large-scale content production.',
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'bg-emerald-100 dark:bg-emerald-900/20',
      iconColor: 'text-emerald-500 dark:text-emerald-400'
    },
    {
      question: 'Which languages and accents are supported?',
      answer: 'Our platform supports over 50 languages and various regional accents, making it perfect for creating multilingual content and reaching global audiences.',
      color: 'from-rose-500 to-pink-500',
      bgColor: 'bg-rose-100 dark:bg-rose-900/20',
      iconColor: 'text-rose-500 dark:text-rose-400'
    },
    {
      question: 'Can I customize the voice characteristics?',
      answer: 'Yes, you can adjust parameters like speed, pitch, and emotion to create the perfect voice for your content. Premium users also have access to advanced customization options.',
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-100 dark:bg-amber-900/20',
      iconColor: 'text-amber-500 dark:text-amber-400'
    },
    {
      question: 'What file formats are supported for audio export?',
      answer: 'We support industry-standard formats including MP3 and WAV, with adjustable quality settings to meet your specific needs.',
      color: 'from-violet-500 to-purple-500',
      bgColor: 'bg-violet-100 dark:bg-violet-900/20',
      iconColor: 'text-violet-500 dark:text-violet-400'
    },
    {
      question: 'Is my content secure when using your service?',
      answer: 'Yes, we use bank-level encryption and strict data protection measures to ensure your content remains private and secure.',
      color: 'from-cyan-500 to-blue-500',
      bgColor: 'bg-cyan-100 dark:bg-cyan-900/20',
      iconColor: 'text-cyan-500 dark:text-cyan-400'
    },
    {
      question: 'How does voice cloning technology work?',
      answer: 'Voice cloning uses AI to analyze and replicate unique voice characteristics, creating a digital voice that matches the original speaker\'s tone and style.',
      color: 'from-teal-500 to-emerald-500',
      bgColor: 'bg-teal-100 dark:bg-teal-900/20',
      iconColor: 'text-teal-500 dark:text-teal-400'
    },
    {
      question: 'What are the commercial usage rights?',
      answer: 'Standard and Premium plans include commercial usage rights for all generated audio content, perfect for business and professional applications.',
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-100 dark:bg-pink-900/20',
      iconColor: 'text-pink-500 dark:text-pink-400'
    },
    {
      question: 'How does real-time voice synthesis work?',
      answer: 'Our advanced cloud infrastructure processes text and generates high-quality audio in real-time, enabling immediate playback and preview.',
      color: 'from-indigo-500 to-purple-500',
      bgColor: 'bg-indigo-100 dark:bg-indigo-900/20',
      iconColor: 'text-indigo-500 dark:text-indigo-400'
    },
    {
      question: 'What is the difference between standard and neural voices?',
      answer: 'Neural voices use advanced AI models to create more natural, expressive speech compared to standard voices, offering superior quality for professional use.',
      color: 'from-emerald-500 to-green-500',
      bgColor: 'bg-emerald-100 dark:bg-emerald-900/20',
      iconColor: 'text-emerald-500 dark:text-emerald-400'
    },
    {
      question: 'How does batch processing work?',
      answer: 'Batch processing allows you to convert multiple texts simultaneously, improving efficiency for large-scale content production.',
      color: 'from-orange-500 to-amber-500',
      bgColor: 'bg-orange-100 dark:bg-orange-900/20',
      iconColor: 'text-orange-500 dark:text-orange-400'
    },
    {
      question: 'What kind of customer support do you offer?',
      answer: 'We offer tiered support based on your plan, ranging from community support for Free users to 24/7 priority support for Premium users.',
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-100 dark:bg-blue-900/20',
      iconColor: 'text-blue-500 dark:text-blue-400'
    },
    {
      question: 'How do you handle data privacy and security?',
      answer: 'We employ enterprise-grade security measures, including encryption, secure data centers, and strict access controls to protect your content.',
      color: 'from-purple-500 to-violet-500',
      bgColor: 'bg-purple-100 dark:bg-purple-900/20',
      iconColor: 'text-purple-500 dark:text-purple-400'
    }
  ];

  // Group FAQs into pairs for the grid
  const faqPairs = faqs.reduce((result, item, index) => {
    if (index % 2 === 0) {
      result.push([item]);
    } else {
      result[result.length - 1].push(item);
    }
    return result;
  }, [] as typeof faqs[]);

  return (
    <section className="py-24 bg-gray-50 dark:bg-dark-900 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 animate-gradient bg-gradient-to-r from-purple-400 via-pink-500 to-blue-500 bg-[length:200%_auto] bg-clip-text text-transparent">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Everything you need to know about our AI voice generation platform.
          </p>
        </div>

        <div className="space-y-6">
          {faqPairs.map((pair, rowIndex) => (
            <div key={rowIndex} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {pair.map((faq, colIndex) => (
                <div
                  key={rowIndex * 2 + colIndex}
                  className={`group bg-white dark:bg-dark-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-200 dark:border-dark-700 ${
                    openRow === rowIndex ? 'ring-2 ring-offset-2 dark:ring-offset-dark-900 ring-gradient-to-r ' + faq.color : ''
                  }`}
                >
                  <button
                    className="w-full text-left"
                    onClick={() => setOpenRow(openRow === rowIndex ? null : rowIndex)}
                  >
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <div className={`w-10 h-10 rounded-lg ${faq.bgColor} flex items-center justify-center mr-4`}>
                            <ChevronDown className={`w-5 h-5 ${faq.iconColor}`} />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:animate-gradient group-hover:bg-[length:200%_auto] group-hover:from-purple-400 group-hover:via-pink-500 group-hover:to-blue-500">
                            {faq.question}
                          </h3>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 flex-shrink-0 ml-4 ${
                            openRow === rowIndex ? 'rotate-180' : ''
                          }`}
                        />
                      </div>
                      <div
                        className={`transition-all duration-200 ease-in-out overflow-hidden ${
                          openRow === rowIndex ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className={`h-px bg-gradient-to-r ${faq.color} mb-4`} />
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Still have questions?
          </p>
          <button 
            onClick={() => {
              navigate('/contact');
              // Ensure scroll to top after navigation
              window.scrollTo(0, 0);
            }} 
            className="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium hover:from-purple-700 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-purple-500/25 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-dark-900"
            aria-label="Go to Contact Us page"
          >
            Contact Support
          </button>
        </div>
      </div>
    </section>
  );
};

export default FAQ;